import React, { useState, useEffect, useCallback } from "react";
import { Spin } from "antd";
import * as recruitmentApi from "../../../../../core/data/redux/services/recruitmentApi";
import { humanReadableDate } from "../../../../../helper/dateTimeHelper";
import { notify } from "../../../../../services/toast.config";

interface ApplicationTrackingTimelineProps {
  applicationId: number;
  applicationCode?: string;
  onBack: () => void;
  primaryColor?: string;
}

const STAGE_META: Record<string, { icon: string; color: string; bg: string; border: string }> = {
  applied: { icon: "ti-send", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  shortlisted: { icon: "ti-star", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  screening: { icon: "ti-eye", color: "#0284c7", bg: "#f0f9ff", border: "#bae6fd" },
  interview: { icon: "ti-calendar-event", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  interview_scheduled: { icon: "ti-calendar-event", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  technical_round: { icon: "ti-code", color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" },
  offer: { icon: "ti-gift", color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" },
  hired: { icon: "ti-circle-check", color: "#047857", bg: "#d1fae5", border: "#6ee7b7" },
  rejected: { icon: "ti-circle-x", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  withdrawn: { icon: "ti-circle-minus", color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" },
};

const getStageMeta = (name: string) => {
  const key = name?.toLowerCase().replace(/\s+/g, "_");
  return STAGE_META[key] ?? { icon: "ti-circle", color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" };
};

const formatStageName = (name: string) =>
  name?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "Unknown";


const STATUS_MAP: Record<string, { label: string; color: string; bg: string; border: string; icon: string }> = {
  applied: { label: "Applied", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", icon: "ti-send" },
  screening: { label: "Screening", color: "#0284c7", bg: "#f0f9ff", border: "#bae6fd", icon: "ti-eye" },
  shortlisted: { label: "Shortlisted", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe", icon: "ti-star" },
  interview: { label: "Interview", color: "#d97706", bg: "#fffbeb", border: "#fde68a", icon: "ti-calendar-event" },
  offer: { label: "Offer", color: "#059669", bg: "#ecfdf5", border: "#a7f3d0", icon: "ti-gift" },
  hired: { label: "Hired", color: "#047857", bg: "#d1fae5", border: "#6ee7b7", icon: "ti-circle-check" },
  rejected: { label: "Rejected", color: "#dc2626", bg: "#fef2f2", border: "#fecaca", icon: "ti-circle-x" },
  withdrawn: { label: "Withdrawn", color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb", icon: "ti-circle-minus" },
};
const getStatusMeta = (s: string) =>
  STATUS_MAP[s?.toLowerCase()] ?? { label: s || "Unknown", color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb", icon: "ti-circle" };

const fmtDateTime = (d?: string) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch { return null; }
};


const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#9ca3af", marginBottom: 5 }}>
      {label}
    </div>
    <div style={{ fontSize: 13.5, fontWeight: 500, color: "#1f2937" }}>{value || "—"}</div>
  </div>
);


const ApplicationTrackingTimeline: React.FC<ApplicationTrackingTimelineProps> = ({
  applicationId,
  applicationCode,
  onBack,
  primaryColor = "#4f46e5",
}) => {
  const [details, setDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);

  const fetchTimeline = useCallback(async () => {
    setLoading(true);
    try {
      let data;
      if (applicationCode) {
        data = await recruitmentApi.trackApplication(applicationCode);
      } else {
        const companyId = localStorage.getItem("company_id") || "";
        const companyType = localStorage.getItem("company_type") || "";
        data = await recruitmentApi.getCandidateApplicationDetails(companyId, companyType, String(applicationId));
      }
      setDetails(data);
    } catch (err: any) {
      notify.error(err?.data?.error?.message || err?.data?.message || err?.message || "Failed to load application timeline");
    } finally {
      setLoading(false);
    }
  }, [applicationId, applicationCode]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  if (loading) return (
    <div style={{ minHeight: 380, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Spin size="large" />
    </div>
  );
  if (!details) return (
    <div style={{ textAlign: "center", padding: "60px 24px", color: "#9ca3af" }}>
      <i className="ti ti-alert-circle" style={{ fontSize: 32, marginBottom: 12, display: "block" }} />
      Unable to load application details.
    </div>
  );

  const appStatus = details.status || details.current_status || "Applied";
  const isRejected = appStatus.toLowerCase().includes("reject");
  const isWithdrawn = appStatus.toLowerCase().includes("withdraw");
  const isTerminal = isRejected || isWithdrawn;
  const statusMeta = getStatusMeta(appStatus);
  const jobTitle = details?.job?.job_title || "Position Title";
  const trackCode = details.application_code || applicationCode;

  const handleWithdraw = async () => {
    setWithdrawing(true);
    try { notify.info("Withdrawal functionality coming soon."); }
    finally { setWithdrawing(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      
      <div style={{
        background: `linear-gradient(135deg, ${primaryColor}08 0%, #fff 60%)`,
        border: `1.5px solid #eaecf0`,
        borderRadius: 16,
        padding: "22px 26px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: primaryColor, marginBottom: 7 }}>
              Application Tracker
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 800, color: "#111827", margin: "0 0 10px", lineHeight: 1.25, letterSpacing: "-0.3px" }}>
              {jobTitle}
            </h3>
            {trackCode && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                fontSize: 12, color: "#6b7280",
                background: "#f8fafc", border: "1px solid #e5e7eb",
                borderRadius: 7, padding: "4px 10px",
                fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              }}>
                <i className="ti ti-hash" style={{ fontSize: 11, color: "#9ca3af" }} />
                {trackCode}
              </span>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 7, flexShrink: 0 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "7px 16px", borderRadius: 20,
              fontSize: 13, fontWeight: 700,
              background: statusMeta.bg, color: statusMeta.color,
              border: `1.5px solid ${statusMeta.border}`,
              boxShadow: `0 2px 8px ${statusMeta.color}18`,
            }}>
              <i className={`ti ${statusMeta.icon}`} style={{ fontSize: 14 }} />
              {statusMeta.label}
            </span>
            <div style={{ fontSize: 12, color: "#9ca3af", display: "flex", alignItems: "center", gap: 4 }}>
              <i className="ti ti-calendar" style={{ fontSize: 12 }} />
              Applied {humanReadableDate(details.applied_at)}
            </div>
          </div>
        </div>
      </div>

      
      {isRejected && (
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 14,
          padding: "16px 20px",
          background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 14,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 11, background: "#fee2e2", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#dc2626", fontSize: 18,
          }}>
            <i className="ti ti-circle-x" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#991b1b", marginBottom: 4 }}>
              Application Not Moving Forward
            </div>
            <p style={{ fontSize: 12.5, color: "#b91c1c", margin: 0, lineHeight: 1.6 }}>
              Thank you for your interest. The hiring team has decided not to proceed at this time. We encourage you to apply for other suitable roles.
            </p>
            {(() => {
              const lastEntry = details.timeline?.[details.timeline.length - 1];
              return lastEntry?.candidate_message ? (
                <div style={{
                  marginTop: 10, padding: "8px 12px",
                  background: "#fff5f5", borderRadius: 8,
                  borderLeft: "3px solid #fca5a5",
                  fontSize: 12.5, color: "#7f1d1d", lineHeight: 1.55,
                  display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10,
                }}>
                  <span>
                    <i className="ti ti-message-circle" style={{ marginRight: 6, fontSize: 12, color: "#dc2626" }} />
                    {lastEntry.candidate_message}
                  </span>
                  {lastEntry.changed_at && (
                    <span style={{ fontSize: 11, color: "#b91c1c", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 3, flexShrink: 0, opacity: 0.8 }}>
                      <i className="ti ti-clock" style={{ fontSize: 11 }} />
                      {fmtDateTime(lastEntry.changed_at)}
                    </span>
                  )}
                </div>
              ) : null;
            })()}
          </div>
        </div>
      )}

      {isWithdrawn && (
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 14,
          padding: "16px 20px",
          background: "#f9fafb", border: "1.5px solid #e5e7eb", borderRadius: 14,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 11, background: "#f3f4f6", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#6b7280", fontSize: 18,
          }}>
            <i className="ti ti-circle-minus" />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#1f2937", marginBottom: 4 }}>Application Withdrawn</div>
            <p style={{ fontSize: 12.5, color: "#6b7280", margin: 0, lineHeight: 1.6 }}>
              You've withdrawn your application. You're welcome to reapply in the future.
            </p>
          </div>
        </div>
      )}

      
      <div style={{
        background: "#fff",
        border: "1.5px solid #eaecf0",
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
      }}>
        
        <div style={{
          padding: "14px 22px",
          borderBottom: "1px solid #f0f0f0",
          background: "#fafafa",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              width: 36, height: 36, borderRadius: 10,
              background: `${primaryColor}14`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: primaryColor, fontSize: 17,
            }}>
              <i className="ti ti-timeline" />
            </span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14.5, color: "#111827" }}>Application Progress</div>
              <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 1 }}>
                {(details.timeline?.length || 0)} event{details.timeline?.length !== 1 ? "s" : ""} recorded
              </div>
            </div>
          </div>
        </div>

        
        <div style={{ padding: "24px 24px 28px", position: "relative" }}>
          
          <div style={{
            position: "absolute",
            left: 45,
            top: 52,
            bottom: 44,
            width: 2,
            background: "linear-gradient(to bottom, #e5e7eb, #f3f4f6)",
            zIndex: 0,
          }} />

          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {(details.timeline || []).map((entry: any, idx: number) => {
              const isLast = idx === (details.timeline.length - 1);
              const meta = getStageMeta(entry.stage_name);
              const isReject = entry.stage_name?.toLowerCase() === "rejected";
              const isWithdr = entry.stage_name?.toLowerCase() === "withdrawn";

              return (
                <div key={idx} style={{
                  display: "flex", gap: 18, position: "relative",
                  zIndex: 1, paddingBottom: isLast ? 0 : 30,
                }}>
                  
                  <div style={{
                    width: 46, height: 46, borderRadius: "50%", flexShrink: 0,
                    background: meta.bg,
                    border: `2px solid ${meta.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: meta.color, fontSize: 18,
                    boxShadow: isLast && !isReject && !isWithdr
                      ? `0 0 0 5px ${meta.color}18` : "none",
                    position: "relative", zIndex: 2,
                  }}>
                    <i className={`ti ${meta.icon}`} />
                    
                    {isLast && !isReject && !isWithdr && (
                      <span style={{
                        position: "absolute", inset: -6, borderRadius: "50%",
                        border: `2px solid ${meta.color}28`,
                        animation: "pulse-ring 2s ease-out infinite",
                        pointerEvents: "none",
                      }} />
                    )}
                  </div>

                  
                  <div style={{ flex: 1, paddingTop: 8 }}>
                    <div style={{
                      display: "flex", alignItems: "flex-start",
                      justifyContent: "space-between", gap: 10,
                      marginBottom: entry.candidate_message ? 6 : 0, flexWrap: "wrap",
                    }}>
                      
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 14.5, fontWeight: 700, color: meta.color }}>
                          {formatStageName(entry.stage_name)}
                        </span>
                        {isLast && !isReject && !isWithdr && (
                          <span style={{
                            fontSize: 10.5, fontWeight: 700, padding: "2px 8px",
                            borderRadius: 20, background: meta.bg, color: meta.color,
                            letterSpacing: "0.06em", border: `1px solid ${meta.border}`,
                          }}>
                            CURRENT
                          </span>
                        )}
                      </div>

                      
                      {entry.changed_at && (
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          fontSize: 11.5, color: meta.color,
                          background: meta.bg, border: `1px solid ${meta.border}`,
                          borderRadius: 7, padding: "3px 9px",
                          fontWeight: 600, whiteSpace: "nowrap",
                        }}>
                          <i className="ti ti-calendar-check" style={{ fontSize: 11 }} />
                          {fmtDateTime(entry.changed_at)}
                        </span>
                      )}
                    </div>

                    
                    {entry.candidate_message && (
                      <div style={{
                        padding: "8px 12px",
                        background: isReject ? "#fff5f5" : meta.bg,
                        borderRadius: 9,
                        borderLeft: `3px solid ${meta.color}60`,
                        fontSize: 12.5,
                        color: isReject ? "#7f1d1d" : "#374151",
                        lineHeight: 1.55,
                      }}>
                        <i className="ti ti-message-circle" style={{ marginRight: 6, color: meta.color, fontSize: 12 }} />
                        {entry.candidate_message}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      
      <div style={{
        background: "#fff", border: "1.5px solid #eaecf0",
        borderRadius: 16, overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
      }}>
        <div style={{
          padding: "14px 22px", borderBottom: "1px solid #f0f0f0",
          background: "#fafafa", display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{
            width: 36, height: 36, borderRadius: 10,
            background: `${primaryColor}14`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: primaryColor, fontSize: 17,
          }}>
            <i className="ti ti-info-circle" />
          </span>
          <div style={{ fontWeight: 700, fontSize: 14.5, color: "#111827" }}>Application Details</div>
        </div>

        <div style={{ padding: "22px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 22 }}>
            <DetailItem label="Applied Date" value={humanReadableDate(details.applied_at)} />
            <DetailItem label="Current Status" value={
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "4px 12px", borderRadius: 20,
                background: statusMeta.bg, color: statusMeta.color,
                fontSize: 12.5, fontWeight: 700,
                border: `1.5px solid ${statusMeta.border}`,
              }}>
                <i className={`ti ${statusMeta.icon}`} style={{ fontSize: 12 }} />
                {statusMeta.label}
              </span>
            } />
            {details.employment_type && <DetailItem label="Job Type" value={details.employment_type} />}
            {details.work_mode && <DetailItem label="Work Mode" value={details.work_mode} />}
            {details.work_location && <DetailItem label="Location" value={details.work_location} />}
            {details.ats_score != null && (
              <DetailItem label="ATS Match Score" value={
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, height: 6, background: "#f3f4f6", borderRadius: 10, overflow: "hidden", maxWidth: 110 }}>
                    <div style={{
                      height: "100%",
                      width: `${details.ats_score}%`,
                      background: details.ats_score >= 80 ? "#22c55e" : details.ats_score >= 60 ? "#f59e0b" : "#ef4444",
                      borderRadius: 10,
                    }} />
                  </div>
                  <span style={{
                    fontSize: 13, fontWeight: 700,
                    color: details.ats_score >= 80 ? "#15803d" : details.ats_score >= 60 ? "#d97706" : "#dc2626",
                  }}>
                    {details.ats_score}%
                  </span>
                </div>
              } />
            )}
          </div>

          
          {!isTerminal && (
            <div style={{
              marginTop: 22, paddingTop: 18,
              borderTop: "1px solid #f3f4f6",
              display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
            }}>
              <div style={{ fontSize: 12.5, color: "#9ca3af", display: "flex", alignItems: "center", gap: 5 }}>
                <i className="ti ti-info-circle" style={{ fontSize: 13 }} />
                Changed your mind? You can withdraw your application at any time.
              </div>
              <button
                onClick={handleWithdraw}
                disabled={withdrawing}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "8px 16px",
                  background: withdrawing ? "#f9fafb" : "#fff5f5",
                  color: withdrawing ? "#9ca3af" : "#dc2626",
                  border: "1.5px solid #fecaca",
                  borderRadius: 10, fontSize: 13, fontWeight: 600,
                  cursor: withdrawing ? "not-allowed" : "pointer",
                  transition: "all 0.15s",
                }}
              >
                <i className="ti ti-x" style={{ fontSize: 13 }} />
                {withdrawing ? "Withdrawing…" : "Withdraw Application"}
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse-ring {
          0%   { transform: scale(1); opacity: 0.5; }
          100% { transform: scale(1.65); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default ApplicationTrackingTimeline;